import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import '../../../component-styles/PublicProposalPage.css';
import API_BASE from '../../../utils/api.js';
import PublicStripePaymentModal from './PublicStripePaymentModal';
import notify from '../../../utils/notify';
import { getTBInvoiceSections, isTBInvoiceListLayout } from '../../../utils/timeBillingShared.js';
import { depositRequestLabel, depositStillDue, formatInvoiceDate } from '../../../utils/invoiceAdjustments.js';

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

const ERROR_MESSAGES = {
  expired: 'This invoice link has expired. Please reach out to your designer to request a new one.',
  not_found: 'This invoice link is no longer active. Please contact your designer for an updated link.',
  network: "We couldn't load this invoice right now. Please try refreshing the page, or contact your designer if the problem continues.",
};

export default function PublicTimeBillingInvoicePage() {
  const { shareToken } = useParams();
  const pdfRef = useRef(null);

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    if (!shareToken) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/timebilling-invoices/public/${encodeURIComponent(shareToken)}`
        );
        if (res.status === 410) throw new Error('expired');
        if (!res.ok) throw new Error('not_found');
        setInvoice(await res.json());
      } catch (err) {
        setError(err.message || 'network');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareToken]);

  useEffect(() => {
    if (!invoice) return;
    const num = invoice.invoiceNumber ? `Invoice #${invoice.invoiceNumber}` : 'Invoice';
    document.title = num;
    return () => { document.title = 'InteriorFolio'; };
  }, [invoice]);

  function downloadPDF() {
    if (!pdfRef.current) return;
    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `Invoice-${invoice.invoiceNumber || invoice.tbInvoiceId}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#fff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      })
      .from(pdfRef.current)
      .save();
  }

  if (loading) return <div className="public-loading">Loading invoice…</div>;
  if (error) return <div className="public-error">{ERROR_MESSAGES[error] || ERROR_MESSAGES.network}</div>;
  if (!invoice) return null;

  const company = invoice.company || {};
  const companyName = company.name || '';
  const logoUrl = company.logo || company.logoUrl || '';
  const addressLine = [company.addressLine1, company.addressLine2].filter(Boolean).join(', ');
  const cityLine = [company.city, company.state, company.zip].filter(Boolean).join(' ');

  // New invoices have explicit billTo* fields; old invoices stored client info inside `company`.
  const hasNewStructure = Boolean(invoice.billToName || invoice.billToStreet);
  const clientName = hasNewStructure ? invoice.billToName : company.name;
  const clientStreet = hasNewStructure ? invoice.billToStreet : company.addressLine1;
  const clientCityLine = hasNewStructure
    ? [invoice.billToCity, invoice.billToState, invoice.billToZip].filter(Boolean).join(' ')
    : cityLine;
  const clientPhone = hasNewStructure ? invoice.billToPhone : company.phone;
  const clientEmail = hasNewStructure ? invoice.billToEmail : company.email;

  const totalAmount = Number(invoice.totals?.total || 0);
  const subtotal = Number(invoice.totals?.subtotal || 0);
  const taxAmount = Number(invoice.totals?.tax || 0);
  const remainingBalance = Math.max(Number((invoice.remainingBalance ?? totalAmount).toFixed(2)), 0);
  const paymentsAmount = Math.max(totalAmount - remainingBalance, 0);
  const isFullyPaid = remainingBalance <= 0;
  const contingencyAmount = Number(invoice.totals?.contingency || 0);
  const discountAmount = Number(invoice.totals?.discount || 0);
  const depositRequested = Number(invoice.totals?.depositRequested || 0);
  const depositDue = isFullyPaid ? 0 : depositStillDue(depositRequested, paymentsAmount);
  const dueDate = formatInvoiceDate(invoice.dueDate);

  const groups = getTBInvoiceSections(invoice);
  const isListLayout = isTBInvoiceListLayout(invoice);
  const hideEmployeeNames = invoice.display?.hideEmployeeNames;

  const applyPaymentEndpoint =
    `${API_BASE}/api/timebilling-invoices/public/${encodeURIComponent(shareToken)}/apply-payment`;
  const createIntentEndpoint =
    `${API_BASE}/api/timebilling-invoices/public/${encodeURIComponent(shareToken)}/create-payment-intent`;

  return (
    <div className="public-proposal-wrapper">
      <header className="public-topbar">
        {logoUrl ? (
          <img
            src={`${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(logoUrl)}`}
            alt={companyName || 'Your designer'}
          />
        ) : companyName ? (
          <span className="public-firm-name">{companyName}</span>
        ) : (
          <span className="public-firm-name">InteriorFolio</span>
        )}
      </header>

      <div className="public-layout">
        <div className="public-content">
          <div id="tb-invoice-content" className="public-proposal-card" ref={pdfRef}>
            <div style={{ padding: '32px 40px' }}>

              {/* Header: designer company left, invoice info right */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                <div>
                  {logoUrl && (
                    <img
                      src={`${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(logoUrl)}`}
                      alt={companyName}
                      style={{ maxHeight: 56, maxWidth: 180, objectFit: 'contain', marginBottom: 8, display: 'block' }}
                    />
                  )}
                  {companyName && <div style={{ fontWeight: 700, fontSize: 15 }}>{companyName}</div>}
                  {!hasNewStructure ? null : (
                    <>
                      {addressLine && <div style={{ fontSize: 13, color: '#555' }}>{addressLine}</div>}
                      {cityLine && <div style={{ fontSize: 13, color: '#555' }}>{cityLine}</div>}
                      {company.phone && <div style={{ fontSize: 13, color: '#555' }}>{company.phone}</div>}
                      {company.email && <div style={{ fontSize: 13, color: '#555' }}>{company.email}</div>}
                    </>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>Time Billing Invoice</div>
                  <div style={{ fontSize: 14, color: '#555', marginTop: 4 }}>#{invoice.invoiceNumber}</div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                    Date: {formatInvoiceDate(invoice.invoiceDate || invoice.createdAt)}
                  </div>
                  {dueDate && <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>Due: {dueDate}</div>}
                  <div style={{ marginTop: 8 }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      background: isFullyPaid ? '#dcfce7' : paymentsAmount > 0 ? '#fef9c3' : '#fee2e2',
                      color: isFullyPaid ? '#166534' : paymentsAmount > 0 ? '#854d0e' : '#991b1b',
                    }}>
                      {isFullyPaid ? 'Paid' : paymentsAmount > 0 ? 'Partially Paid' : 'Unpaid'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Bill To */}
              {(clientName || clientStreet || clientCityLine || clientPhone || clientEmail) && (
                <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: 4 }}>
                    Bill To
                  </div>
                  {clientName && <div style={{ fontWeight: 600, fontSize: 14, color: '#111' }}>{clientName}</div>}
                  {clientStreet && <div style={{ fontSize: 13, color: '#555' }}>{clientStreet}</div>}
                  {clientCityLine && <div style={{ fontSize: 13, color: '#555' }}>{clientCityLine}</div>}
                  {clientPhone && <div style={{ fontSize: 13, color: '#555' }}>{clientPhone}</div>}
                  {clientEmail && <div style={{ fontSize: 13, color: '#555' }}>{clientEmail}</div>}
                </div>
              )}

              {/* Time entry groups */}
              {groups.map((group) => (
                <div key={group.heading} style={{ marginBottom: 24 }}>
                  {group.heading && (
                    <div style={{ fontWeight: 600, fontSize: 14, paddingBottom: 6, borderBottom: '2px solid #e5e7eb', marginBottom: 8 }}>
                      {group.heading}
                    </div>
                  )}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                        {isListLayout && <th style={{ textAlign: 'left', padding: '4px 0', fontWeight: 500 }}>Activity</th>}
                        {!hideEmployeeNames && <th style={{ textAlign: 'left', padding: '4px 0', fontWeight: 500 }}>Employee</th>}
                        <th style={{ textAlign: 'left', padding: '4px 0', fontWeight: 500 }}>Description</th>
                        <th style={{ textAlign: 'right', padding: '4px 0', fontWeight: 500 }}>Hours</th>
                        <th style={{ textAlign: 'right', padding: '4px 0', fontWeight: 500 }}>Rate</th>
                        <th style={{ textAlign: 'right', padding: '4px 0', fontWeight: 500 }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(group.items || []).map((item) => (
                        <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          {isListLayout && (
                            <td style={{ padding: '6px 0', color: '#374151' }}>{item.activity || '—'}</td>
                          )}
                          {!hideEmployeeNames && (
                            <td style={{ padding: '6px 0', color: '#374151' }}>{item.employee || '—'}</td>
                          )}
                          <td style={{ padding: '6px 0', color: '#374151' }}>{item.description || '—'}</td>
                          <td style={{ padding: '6px 0', textAlign: 'right', color: '#374151' }}>
                            {Number(item.hours).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: '6px 0', textAlign: 'right', color: '#374151' }}>
                            {formatCurrency(item.rate)}/hr
                          </td>
                          <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 500 }}>
                            {formatCurrency(item.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              {/* Totals */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <table style={{ fontSize: 13, minWidth: 240 }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '3px 16px 3px 0', color: '#6b7280', textAlign: 'right' }}>Subtotal</td>
                      <td style={{ padding: '3px 0', textAlign: 'right', color: '#1a1a1a' }}>{formatCurrency(subtotal)}</td>
                    </tr>
                    {contingencyAmount > 0 && (
                      <tr>
                        <td style={{ padding: '3px 16px 3px 0', color: '#6b7280', textAlign: 'right' }}>
                          {invoice.contingency?.label || 'Contingency'}
                          {invoice.contingency?.percent ? ` (${invoice.contingency.percent}%)` : ''}
                        </td>
                        <td style={{ padding: '3px 0', textAlign: 'right', color: '#1a1a1a' }}>{formatCurrency(contingencyAmount)}</td>
                      </tr>
                    )}
                    {discountAmount > 0 && (
                      <tr>
                        <td style={{ padding: '3px 16px 3px 0', color: '#6b7280', textAlign: 'right' }}>
                          Discount{invoice.discount?.type === 'percent' ? ` (${invoice.discount.value}%)` : ''}
                        </td>
                        <td style={{ padding: '3px 0', textAlign: 'right', color: '#1a1a1a' }}>−{formatCurrency(discountAmount)}</td>
                      </tr>
                    )}
                    {taxAmount > 0 && (
                      <tr>
                        <td style={{ padding: '3px 16px 3px 0', color: '#6b7280', textAlign: 'right' }}>
                          Sales Tax {invoice.tax?.location?.name ? `(${invoice.tax.location.name})` : ''}
                        </td>
                        <td style={{ padding: '3px 0', textAlign: 'right', color: '#1a1a1a' }}>{formatCurrency(taxAmount)}</td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ padding: '3px 16px 3px 0', color: '#6b7280', textAlign: 'right' }}>Total</td>
                      <td style={{ padding: '3px 0', textAlign: 'right', color: '#1a1a1a' }}>{formatCurrency(totalAmount)}</td>
                    </tr>
                    {paymentsAmount > 0 && (
                      <tr>
                        <td style={{ padding: '3px 16px 3px 0', color: '#6b7280', textAlign: 'right' }}>Payments</td>
                        <td style={{ padding: '3px 0', textAlign: 'right', color: '#166534' }}>−{formatCurrency(paymentsAmount)}</td>
                      </tr>
                    )}
                    <tr style={{ borderTop: '2px solid #e5e7eb' }}>
                      <td style={{ padding: '8px 16px 3px 0', fontWeight: 700, fontSize: 14, textAlign: 'right' }}>Balance Due</td>
                      <td style={{ padding: '8px 0 3px 0', fontWeight: 700, fontSize: 14, textAlign: 'right', color: isFullyPaid ? '#166534' : '#1a1a1a' }}>
                        {formatCurrency(remainingBalance)}
                      </td>
                    </tr>
                    {depositRequested > 0 && (
                      <tr>
                        <td style={{ padding: '3px 16px 3px 0', color: '#6b7280', textAlign: 'right' }}>
                          {depositRequestLabel(invoice.depositRequest)}
                        </td>
                        <td style={{ padding: '3px 0', textAlign: 'right', color: '#1a1a1a' }}>{formatCurrency(depositRequested)}</td>
                      </tr>
                    )}
                    {depositDue > 0 && (
                      <tr>
                        <td style={{ padding: '3px 16px 3px 0', fontWeight: 700, textAlign: 'right' }}>Deposit Due Now</td>
                        <td style={{ padding: '3px 0', fontWeight: 700, textAlign: 'right', color: '#1a1a1a' }}>{formatCurrency(depositDue)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #e5e7eb', fontSize: 11, color: '#9ca3af', textAlign: 'center' }}>
                Invoice provided by InteriorFolio
              </div>

            </div>
          </div>
        </div>
      </div>

      <div className="public-actions">
        {!isFullyPaid && (
          <button className="approve-btn" onClick={() => setShowPayment(true)}>
            Pay Now
          </button>
        )}
        <button className="pdf-btn" onClick={downloadPDF}>
          Download PDF
        </button>
      </div>

      {showPayment && (
        <PublicStripePaymentModal
          shareToken={shareToken}
          remainingBalance={remainingBalance}
          defaultAmount={depositDue}
          invoiceNumber={invoice.invoiceNumber}
          createIntentEndpoint={createIntentEndpoint}
          applyPaymentEndpoint={applyPaymentEndpoint}
          onSuccess={(amount, data) => {
            setInvoice((prev) => ({
              ...prev,
              remainingBalance: data?.remainingBalance ?? Math.max((prev.remainingBalance ?? 0) - amount, 0),
            }));
            setShowPayment(false);
            notify('Payment submitted successfully');
          }}
          onClose={() => setShowPayment(false)}
        />
      )}
    </div>
  );
}
